import type { VehicleStatus } from "../types/api"
import { localizeStatusFrameLabel } from "../i18n/statusTitles"
import { useAuthStore } from "../store/authStore"
import { useLocaleStore } from "../store/localeStore"
import { FINAL_STATUS_VISUALS } from "./StatusVisuals"

export type StatusFrameStop = {
  offset: number
  color: string
}

export type StatusFramePresentation = {
  label: string
  gradient: string
  stops: readonly StatusFrameStop[]
}

const MASTER_STATUS_FRAME_STOPS = [
  { offset: 0, color: "#773DBE" },
  { offset: 0.32, color: "#6555D7" },
  { offset: 0.47, color: "#008FEA" },
  { offset: 0.67, color: "#008FEA" },
  { offset: 0.82, color: "#6555D7" },
  { offset: 1, color: "#773DBE" },
] as const

const MASTER_STATUS_FRAME_GRADIENT =
  "linear-gradient(90deg, #773DBE 0%, #6555D7 32%, #008FEA 47%, #008FEA 67%, #6555D7 82%, #773DBE 100%)"

/**
 * Runtime Status Frame mapping.
 *
 * The persisted runtime statuses use the current approved product palette in
 * FINAL_STATUS_VISUALS. The same two-stop pair is shared by the Status choice-circle
 * preview and the selected-status frame. NoStatus is represented by null and has no frame.
 *
 * AutoClan is dynamic. The persisted public VehicleCard already receives the clan
 * name from the backend; while the user is previewing the generic AutoClan status
 * before Apply, use the clan resolved during auth bootstrap so the preview is also
 * the real registered name and never flashes the generic status title.
 */
export function resolveStatusFrame(status: VehicleStatus | null | undefined): StatusFramePresentation | null {
  if (!status) return null

  const slug = status.slug.trim().toLowerCase()
  const title = status.title.trim()
  if (!title) return null
  const locale = useLocaleStore.getState().locale

  if (slug === "special-series") {
    if (!import.meta.env.DEV) return null
    return {
      label: title.toLocaleUpperCase(locale),
      gradient: MASTER_STATUS_FRAME_GRADIENT,
      stops: MASTER_STATUS_FRAME_STOPS,
    }
  }

  const visual = FINAL_STATUS_VISUALS[slug]
  if (!visual || slug === "suggest-own" || slug === "suggest_own") {
    if (!visual && import.meta.env.DEV) {
      console.warn(`[Status Frame] Missing runtime gradient for status: ${status.slug}`)
    }
    return null
  }

  // Product rule: Status Frame uses a center-out ("diamond") color layout.
  // The first approved color is the visual center behind the label; the second
  // approved color is mirrored to both outer edges.
  const isAutoclan = slug === "autoclan" || slug === "clan_member"
  const bootstrapClanName = isAutoclan
    ? (useAuthStore.getState().autoclanDetail?.name || useAuthStore.getState().autoclans[0]?.name || "").trim()
    : ""
  const localizedFrameLabel = localizeStatusFrameLabel(locale, slug, visual.frameLabel ?? title)
  const frameLabel = isAutoclan
    ? (bootstrapClanName || localizedFrameLabel || title)
    : localizedFrameLabel.toLocaleUpperCase(locale)

  return {
    label: frameLabel,
    gradient: `linear-gradient(90deg, ${visual.fill[1]} 0%, ${visual.fill[0]} 50%, ${visual.fill[1]} 100%)`,
    stops: [
      { offset: 0, color: visual.fill[1] },
      { offset: 0.5, color: visual.fill[0] },
      { offset: 1, color: visual.fill[1] },
    ],
  }
}
